import { Button, Tag, Tooltip, Dropdown } from "antd";
import React, { useState } from "react";
import { FileTextOutlined, BookOutlined, DownOutlined, AlertOutlined } from '@ant-design/icons';
import { FieldLabels } from "../../constants/fieldLabels";


/**
 * Get status badge styling to match PACS page
 */
const getStatusStyle = (status: string) => {
  const baseStyle = {
    height: '20px',
    borderRadius: '4px',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '20px',
    letterSpacing: '0.06px',
    verticalAlign: 'middle',
    border: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  switch (status) {
    case 'SCANNED':
      return {
        ...baseStyle,
        width: '70px',
        padding: '0 6px 0 6px',
        background: '#DEF0FF',
        color: '#003FD2'
      };
    case 'SIGNEDOFF':
      return {
        ...baseStyle,
        width: '80px',
        padding: '0 6px 0 6px',
        background: '#DAF3EE',
        color: '#00795C'
      };
    case 'DRAFTED':
      return {
        ...baseStyle,
        width: '65px',
        padding: '0 6px 0 6px',
        background: '#FEECD5',
        color: '#AC5000'
      };
    case 'PENDING':
      return {
        ...baseStyle,
        width: '70px',
        padding: '0 6px 0 6px',
        background: '#FEE2E2',
        color: '#991B1B'
      };
    default:
      return {
        ...baseStyle,
        width: 'auto',
        padding: '0 6px 0 6px',
        background: '#DEF0FF',
        color: '#003FD2'
      };
  }
};

export const orderColumns = () => [
  {
    dataIndex: 'po_pin',
    key: 'po_pin',
    title: FieldLabels.PATIENT_ID,
    width: 105,
    render: (text, rec) => {
      const patPin = rec?.patient?.pat_pin;
      return (
        <Tooltip title={patPin}>
          <div
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%',
            }}
          >
            {patPin}
          </div>
        </Tooltip>
      );
    },
  },
  {
    dataIndex: 'po_pat_name',
    title: FieldLabels.PATIENT_NAME,
    render: (text, record, openReportEditor, openViewer) => {
      const isEmergency = record.po_priority?.toUpperCase() === 'EMERGENCY';
      const patientName = record.patient?.pat_name;

      return (
        <div>
          <Button
            className="ms-1auto d-flex align-items-center ph-0 col-pat-name"
            type="link"
            style={{ padding: 0 }}
          >
            <Tooltip title={patientName}>
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'inline-block',
                  maxWidth: isEmergency ? 'calc(100% - 22px)' : '100%',
                }}
              >
                {patientName}
              </span>
            </Tooltip>
            {isEmergency && (
              <Tooltip title="Emergency Case">
                <AlertOutlined
                  style={{
                    color: '#FF4D4F',
                    marginLeft: '6px',
                    fontSize: '14px',
                    animation: 'pulse 2s ease-in-out infinite',
                  }}
                />
              </Tooltip>
            )}
          </Button>
        </div>
      );
    },
    width: 250,
  },
  {
    dataIndex: 'modality',
    key: 'modality',
    title: FieldLabels.MODALITY,
    width: 100,
    render: (text, rec) => (
      <span
        style={{
          background: '#F5F5F5',
          border: '1px solid #EAEEF2',
          height: '24px',
          borderRadius: '4px',
          padding: '2px 8px',
          display: 'inline-block',
          lineHeight: '20px',
        }}
      >
        {rec?.po_modality}
      </span>
    ),
  },
  {
    dataIndex: 'po_body_part',
    title: FieldLabels.BODY_PART,
    width: 120,
    render: (text, rec) => rec?.po_body_part,
  },
  {
    dataIndex: 'po_diag_desc',
    title: FieldLabels.STUDY_DESCRIPTION_SHORT,
    width: 260,
    render: (txt, rec) => {
      return (
        <Tooltip title={rec?.po_priority}>
          <span>{rec?.po_diag_desc}</span>
        </Tooltip>
      );
    },
  },
  {
    dataIndex: 'po_ord_no',
    title: FieldLabels.ORDER_NUMBER,
    width: 100,
    render: (text, rec) => rec?.po_his_ord_no,
  },
  {
    dataIndex: 'po_acc_no',
    title: FieldLabels.ACCESSION_NUMBER_SHORT,
    width: 120,
    render: (text, rec) => rec?.po_acc_no,
  },
  // {
  //   dataIndex: "po_site",
  //   title: "Site",
  //   width: 120,
  //   render: (text, rec) => rec?.po_site
  // },
  {
    dataIndex: 'po_assigned_to',
    title: FieldLabels.ASSIGNED_TO,
    width: 120,
    render: (val, rec) => {
      return `${rec?.order_workflow?.ow_assigned_technician || ''}`;
    },
  },
  {
    dataIndex: 'po_ref_doc',
    title: FieldLabels.REFERRING_DOCTOR,
    width: 182,
    render: (text, rec) => {
      const refDoc = rec?.po_ref_doc;
      return (
        <Tooltip title={refDoc}>
          <div
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%',
            }}
          >
            {refDoc}
          </div>
        </Tooltip>
      );
    },
  },
  {
    dataIndex: 'po_status',
    title: FieldLabels.STATUS,
    render: (text, record) => {
      const pacs_status = record?.order_workflow?.ow_pacs_status;
      return (
        <Tag
          className="custom-status-tag"
          style={getStatusStyle(pacs_status)}
        >
          {pacs_status?.replaceAll('_', ' ')}
        </Tag>
      );
    },
  },
];


export const SavedSearches = ({ savedFilters, handleFilterSelection, quickFilters, handleQuickFilters, currentFilters = {} }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [flashingButton, setFlashingButton] = useState(null);

  // Style variants based on filter type
  const typeStyles = {
    'TOGGLE': {
      // Purple style for toggle buttons (Emergency, Re-reported)
      background: "#FFFFFF",
      border: "1px solid #D1D5DB",
      color: "#374151",
      selectedBackground: "#F7EFFF",
      selectedBorder: "#7D48B0",
      selectedColor: "#400A73"
    },
    'DATE': {
      // Blue style for date buttons (Today, Yesterday, Last 3 days)
      background: "#EFF6FF",
      border: "1px solid #BFDBFF",
      color: "#1447E6",
      selectedBackground: "#DBEAFE",
      selectedBorder: "#1447E6",
      selectedColor: "#1447E6"
    },
    'NA': {
      // Green style for modality buttons (CT, MR, X-Ray, Mammo)
      background: "#EFFDF4",
      border: "1px solid #B9F8CF",
      color: "#008439",
      selectedBackground: "#D1FAE5",
      selectedBorder: "#008439",
      selectedColor: "#008439"
    }
  };

  // Get style for a specific filter based on its type and selected state
  const getFilterStyle = (filter) => {
    const filterType = filter.qf_type || 'NA';
    const style = typeStyles[filterType] || typeStyles['NA'];

    // For TOGGLE buttons, use currentFilters to show persistent selection
    // For DATE and modality buttons, use flashingButton for temporary highlight
    let isHighlighted = false;
    if (filterType === 'TOGGLE') {
      isHighlighted = currentFilters[filter.qf_key];
    } else {
      isHighlighted = flashingButton === filter.qf_id;
    }

    return {
      height: '20px',
      padding: '2px 8px',
      borderRadius: '4px',
      background: isHighlighted ? style.selectedBackground : style.background,
      border: isHighlighted ? `1px solid ${style.selectedBorder}` : style.border,
      fontFamily: 'Inter, sans-serif',
      fontWeight: '500',
      fontSize: '12px',
      lineHeight: '16px',
      letterSpacing: '0.04px',
      color: isHighlighted ? style.selectedColor : style.color,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      whiteSpace: 'nowrap',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    };
  };

  // Group filters by type and sort within groups
  const groupedFilters = quickFilters.reduce((acc, filter) => {
    const type = filter.qf_type || 'NA';
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(filter);
    return acc;
  }, {});

  // Sort each group by qf_order
  Object.keys(groupedFilters).forEach(type => {
    groupedFilters[type].sort((a, b) => (a.qf_order || 0) - (b.qf_order || 0));
  });

  // Define type order for display: TOGGLE, DATE, then modality (NA)
  const typeOrder = ['TOGGLE', 'DATE', 'NA'];

  // Create dropdown menu items from saved filters
  const dropdownItems = savedFilters.map((filter, index) => ({
    key: filter.uf_name,
    label: (
      <div onClick={() => handleFilterSelection(filter)} style={{ padding: '8px 12px' }}>
        {filter.uf_name}
      </div>
    ),
  }));

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      marginBottom: '16px',
      flexWrap: 'wrap'
    }}>
      {/* Saved Views Dropdown */}
      <Dropdown
        menu={{ items: dropdownItems }}
        trigger={['click']}
        open={dropdownOpen}
        onOpenChange={setDropdownOpen}
        placement="bottomLeft"
      >
        <Button
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            width: '142px',
            height: '30px',
            padding: '5px 10px',
            borderRadius: '8px',
            border: '1px solid #E2E8F1',
            background: '#FAFAFA',
            fontFamily: 'Inter Variable, Inter, sans-serif',
            fontWeight: '600',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.04px',
            color: '#000000'
          }}
          onClick={(e) => e.preventDefault()}
        >
          <BookOutlined style={{ width: '16px', height: '16px', fontSize: '16px' }} />
          Saved views
          <DownOutlined style={{ fontSize: '12px', marginLeft: 'auto' }} />
        </Button>
      </Dropdown>

      {/* Quick Filters Section - Grouped by type */}
      {quickFilters && quickFilters.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <span style={{
            fontFamily: 'Inter Variable, Inter, sans-serif',
            fontWeight: '600',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.04px',
            color: '#000000',
            marginRight: '4px'
          }}>
            Quick:
          </span>

          {/* Render filters grouped by type */}
          {typeOrder.map(type => {
            const filtersOfType = groupedFilters[type] || [];
            if (filtersOfType.length === 0) return null;

            return (
              <div key={type} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {filtersOfType.map((filter) => {
                  const handleClick = () => {
                    handleQuickFilters(filter);

                    // Flash effect for DATE and modality buttons only
                    if (filter.qf_type !== 'TOGGLE') {
                      setFlashingButton(filter.qf_id);
                      setTimeout(() => {
                        setFlashingButton(null);
                      }, 1000);
                    }
                  };

                  return (
                    <button
                      key={filter.qf_id}
                      onClick={handleClick}
                      style={getFilterStyle(filter)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = '0.85';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = '1';
                      }}
                    >
                      {filter.qf_name}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  )
}

// Old QuickFilters component - COMMENTED OUT - Now using SavedSearches component for quick filters
// This component was at wrong placement, replaced with dynamic quick filters in SavedSearches component
// export const QuickFilters = ({ quickFilters, handleQuickFilters }) => {
//   return (
//     <div className="mb-2">
//       {quickFilters.map((filter, index) => (
//         <span className="quick-filters ml-3" key={filter.qf_name} onClick={() => handleQuickFilters(filter)}>
//           {filter.qf_name}
//         </span>
//       ))}
//     </div>
//   )
// }
